import React, { useState, useMemo } from 'react';
// FIX: Use namespace import for react-router-dom to fix potential module resolution errors.
import * as ReactRouterDom from 'react-router-dom';
import { COST_CATEGORIES, SUB_ITEM_FORM_CONFIG } from '../constants';
import { Project, ExpenseEntry, MainCostCategory } from '../types';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ArrowLeftIcon } from '../components/icons/ArrowLeftIcon';
import { SearchIcon } from '../components/icons/SearchIcon';
import { PlusIcon } from '../components/icons/PlusIcon';
import { EditIcon } from '../components/icons/EditIcon';
import { TrashIcon } from '../components/icons/TrashIcon';
import { ChevronLeftIcon } from '../components/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '../components/icons/ChevronRightIcon';
import { DynamicEntryModal } from '../components/DynamicEntryModal';
import { EyeIcon } from '../components/icons/EyeIcon';
import { useExpenses } from '../hooks/useExpenses';
import { useProjects } from '../hooks/useProjects';
import { useUsers } from '../hooks/useUsers';
import { DeleteConfirmationModal } from '../components/DeleteConfirmationModal';

const ITEMS_PER_PAGE = 10;

export const SubItemDetailPage: React.FC = () => {
    const { obraId, categoryId, subItemId } = ReactRouterDom.useParams<{ obraId: string; categoryId: string; subItemId: string }>();
    const navigate = ReactRouterDom.useNavigate();
    const { allExpenses, saveExpense, deleteExpense, deleteExpenses } = useExpenses();
    const { projects } = useProjects();
    const { users } = useUsers();
    
    // Component-level state for UI
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingExpense, setEditingExpense] = useState<ExpenseEntry | null>(null);
    const [expenseToDelete, setExpenseToDelete] = useState<ExpenseEntry | null>(null);

    // Memoized selectors
    const project = useMemo(() => projects.find(p => p.id === obraId), [projects, obraId]);
    const isLocked = project?.isLocked;
    const config = useMemo(() => subItemId ? SUB_ITEM_FORM_CONFIG[subItemId] : null, [subItemId]);
    const categoryDetails = useMemo(() => COST_CATEGORIES.find(c => c.id === categoryId), [categoryId]);
    const subItemDetails = useMemo(() => categoryDetails?.subItems.find(si => si.id === subItemId), [categoryDetails, subItemId]);
    const userMap = useMemo(() => new Map(users.map(u => [u.id, u.name])), [users]);
    
    const expenses = useMemo(() => {
        if (!subItemId || !obraId) return [];
        return allExpenses
            .filter(e => e.projectId === obraId && e.subCategoryItemId === subItemId)
            .sort((a, b) => (b.month || '').localeCompare(a.month || ''));
    }, [allExpenses, obraId, subItemId]);


    const filteredExpenses = useMemo(() => {
        if (!searchTerm) return expenses;
        return expenses.filter(exp => 
            Object.values(exp).some(val => 
                String(val).toLowerCase().includes(searchTerm.toLowerCase())
            )
        );
    }, [expenses, searchTerm]);

    const paginatedExpenses = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredExpenses.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredExpenses, currentPage]);

    const totalPages = Math.ceil(filteredExpenses.length / ITEMS_PER_PAGE);

    const handleOpenAddModal = () => {
        setEditingExpense(null);
        setIsModalOpen(true);
    };

    const handleOpenEditModal = (expense: ExpenseEntry) => {
        setEditingExpense(expense);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingExpense(null);
    };

    const handleSaveExpense = async (dataFromModal: any) => {
        // Check if this is a recurring series extension or rateio payload
        if ('updates' in dataFromModal && 'creations' in dataFromModal) {
            handleCloseModal();
            const isRateio = dataFromModal.updates.length === 0;
            const hasDeletions = dataFromModal.deletions && dataFromModal.deletions.length > 0;
            
            alert(isRateio ? 'Processando o rateio...' : 'Processando a série de lançamentos...');
            try {
                // Execute deletions if any
                if (hasDeletions) {
                    await deleteExpenses(dataFromModal.deletions);
                }

                // Execute all update operations in parallel
                if (dataFromModal.updates.length > 0) {
                    const updatePromises = dataFromModal.updates.map((exp: Partial<ExpenseEntry>) => saveExpense(exp));
                    const results = await Promise.all(updatePromises);
                    const failed = results.find(r => !r.success);
                    if (failed) {
                        throw new Error(failed.message || 'Falha ao atualizar lançamentos.');
                    }
                }
                
                // Execute creation operation
                if (dataFromModal.creations.length > 0) {
                    // Send individually in parallel to avoid payload size limits (e.g. Nginx 1MB limit)
                    const creationPromises = dataFromModal.creations.map((exp: Partial<ExpenseEntry>) => saveExpense([exp]));
                    const results = await Promise.all(creationPromises);
                    const failed = results.find(r => !r.success);
                    if (failed) {
                        throw new Error(failed.message || 'Falha ao salvar lançamentos.');
                    }
                }
                alert(isRateio ? 'Rateio processado com sucesso!' : 'Série de lançamentos processada com sucesso!');
            } catch(e) {
                console.error("Failed to process bulk expense operation", e);
                alert(`Ocorreu um erro ao processar a operação: ${(e as Error).message}`);
            }
            return; // End execution for bulk operations
        }

        // --- Standard save logic for single entries ---
        const payload: Partial<ExpenseEntry> = { ...dataFromModal };
        const isEditing = !!payload.id;
        
        if (!isEditing) {
            payload.projectId = obraId;
            payload.mainCategory = categoryId as MainCostCategory;
            payload.subCategoryItemId = subItemDetails!.id;
            payload.subCategoryItemName = subItemDetails!.name;
        }
        
        const result = await saveExpense(payload);
    
        if (result.success) {
            handleCloseModal();
            setSearchTerm('');
            setCurrentPage(1);
        } else {
            alert(`Ocorreu uma falha ao salvar o lançamento:\n\n${result.message || 'Erro desconhecido. Verifique o console.'}`);
        }
    };
    
    const handleOpenDeleteModal = (expense: ExpenseEntry) => {
      setExpenseToDelete(expense);
    };

    const handleConfirmDelete = () => {
      if (expenseToDelete) {
        deleteExpense(expenseToDelete.id);
        setExpenseToDelete(null);
      }
    };

    const handleViewDetails = (expenseId: string) => {
        navigate(`/obras/${obraId}/custos/${categoryId}/${subItemId}/${expenseId}`);
    };

    if (!project || !categoryDetails || !subItemDetails || !config) {
        return <div className="text-center py-10">Dados de configuração não encontrados.</div>;
    }

    const { tableColumns } = config;

    const finalTableColumns = useMemo(() => {
        return tableColumns.map(col => {
            if (col.key === 'requesterId') {
                return {
                    ...col,
                    render: (value: any) => userMap.get(value) || value || 'N/A'
                };
            }
            return col;
        });
    }, [tableColumns, userMap]);

    return (
        <div className="text-gray-900 dark:text-dark-text-primary">
            {/* Header */}
            <div className="flex items-center mb-6">
                <Button variant="ghost" size="sm" onClick={() => navigate(`/obras/${obraId}/custos/${categoryId}`)} className="p-2 mr-2 hover:bg-gray-100 dark:hover:bg-dark-tertiary" aria-label="Voltar">
                    <ArrowLeftIcon className="text-2xl" />
                </Button>
                <h1 className="text-xl md:text-2xl font-semibold">
                    {categoryDetails.id.split(' - ')[0]} &gt; <span className="text-brand-lime">{subItemDetails.name}</span>
                </h1>
            </div>

            {/* Main Card */}
            <Card footer={
                 totalPages > 1 && (
                    <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 dark:text-dark-text-secondary">Página {currentPage} de {totalPages}</span>
                        <div className="flex items-center space-x-2">
                             <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                                <ChevronLeftIcon className="text-xl" />
                            </Button>
                             <Button variant="ghost" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                                <ChevronRightIcon className="text-xl" />
                            </Button>
                        </div>
                    </div>
                )
            }>
                {/* Search and Add controls */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-4 gap-4">
                    <div className="relative w-full md:max-w-sm">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <SearchIcon className="text-xl text-gray-400" />
                        </div>
                        <Input id="searchEntries" type="text" placeholder="Pesquisar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 w-full" containerClassName="mb-0" />
                    </div>
                    <Button variant="primary" onClick={handleOpenAddModal} leftIcon={<PlusIcon className="text-xl" />} disabled={isLocked}>
                        Adicionar
                    </Button>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-tertiary">
                        <thead className="bg-gray-50 dark:bg-dark-secondary">
                            <tr>
                                {finalTableColumns.map(col => (
                                    <th key={String(col.key)} className={`px-3 py-3 text-xs font-medium text-gray-500 dark:text-dark-text-secondary uppercase tracking-wider ${col.key === 'actions' ? 'text-center' : 'text-left'}`}>{col.header}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-dark-surface divide-y divide-gray-200 dark:divide-dark-tertiary">
                            {paginatedExpenses.length > 0 ? paginatedExpenses.map(exp => (
                                <tr key={exp.id} className="hover:bg-gray-50 dark:hover:bg-dark-secondary cursor-pointer" onClick={() => handleViewDetails(exp.id)}>
                                    {finalTableColumns.map(col => (
                                        <td key={`${exp.id}-${String(col.key)}`} className="px-3 py-3 whitespace-nowrap text-sm text-gray-600 dark:text-dark-text-secondary">
                                            {col.key === 'actions' ? (
                                                <div className="text-center">
                                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleViewDetails(exp.id); }} className="p-1" title="Visualizar"><EyeIcon className="text-xl"/></Button>
                                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleOpenEditModal(exp); }} className="p-1" title="Editar" disabled={isLocked}><EditIcon className="text-xl"/></Button>
                                                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleOpenDeleteModal(exp); }} className="p-1 text-red-500" title="Excluir" disabled={isLocked}><TrashIcon className="text-xl"/></Button>
                                                </div>
                                            ) : col.render ? (
                                                col.render(exp[col.key as keyof ExpenseEntry], exp)
                                            ) : (
                                                <span className="truncate max-w-xs block" title={String(exp[col.key as keyof ExpenseEntry] || '')}>
                                                  {String(exp[col.key as keyof ExpenseEntry] || '')}
                                                </span>
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={finalTableColumns.length} className="text-center py-10 text-gray-500 dark:text-dark-text-secondary">
                                        {searchTerm ? "Nenhum resultado encontrado." : "Nenhum lançamento para este item."}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {isModalOpen && subItemId && obraId && (
                <DynamicEntryModal
                    isOpen={isModalOpen}
                    onClose={handleCloseModal}
                    onSubmit={handleSaveExpense}
                    existingExpense={editingExpense}
                    subItemId={subItemId}
                    obraId={obraId}
                    categoryId={categoryId}
                    allExpensesForSeries={allExpenses}
                    isLocked={isLocked}
                />
            )}
            
            {expenseToDelete && (
              <DeleteConfirmationModal
                isOpen={!!expenseToDelete}
                onClose={() => setExpenseToDelete(null)}
                onConfirm={handleConfirmDelete}
                message={`Você tem certeza que deseja excluir este lançamento? Fornecedor: ${expenseToDelete.supplier || 'N/A'}, Nota Fiscal: ${expenseToDelete.invoice || 'N/A'}.`}
              />
            )}
        </div>
    );
};